import React, { FC, useState, useCallback, useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { DialogActions, DialogContent, Divider } from '@mui/material';
import styled from 'styled-components';
import Dialog from 'components/UI/Dialog';
import Button from 'components/UI/Button';
import Input from 'components/UI/Input';
import Grid from 'components/UI/Grid';
import Text from 'components/UI/Text';
import { theme } from 'components/UI';
import { useTranslation } from 'react-i18next';
import { useStores } from 'stores/hooks/hooks';
import Space from 'components/UI/Space';
import { DialogOnCloseReason } from 'models/shared';
import { NotificationTemplate } from 'models/restTypes';

const NoWrappingText = styled(Text)`
  white-space: nowrap;
  min-width: 30%;
  max-width: 30%;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 8px;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  background-color: var(--surface-ground);
  color: var(--text-color);
  font-family: inherit;
  font-size: 14px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  &::placeholder {
    color: var(--text-color-secondary);
  }
`;

interface TemplateEdit {
  imageUri: string;
  templateText: string;
}

interface NotificationTemplateEditDialogProps {
  onHide: () => void;
  visible: boolean;
  title: string;
}

const NotificationTemplateEditDialog: FC<NotificationTemplateEditDialogProps> =
  observer(({ visible = false, onHide, title }) => {
    const { t } = useTranslation('staticData');
    const { notificationStore } = useStores();
    const [templateEdit, setTemplateEdit] = useState<TemplateEdit>({
      imageUri: notificationStore.selectedTemplate?.imageUri || '',
      templateText: notificationStore.selectedTemplate?.templateText || '',
    });

    // Load selected template data when dialog opens
    useEffect(() => {
      if (visible && notificationStore.selectedTemplate) {
        setTemplateEdit({
          imageUri: notificationStore.selectedTemplate.imageUri || '',
          templateText: notificationStore.selectedTemplate.templateText || '',
        });
      }
    }, [visible, notificationStore.selectedTemplate]);

    const isValidForm = useMemo(() => {
      // At least one field should have content
      return (
        templateEdit.imageUri.trim().length > 0 ||
        templateEdit.templateText.trim().length > 0
      );
    }, [templateEdit]);

    const closeDialog = useCallback(() => {
      setTemplateEdit({
        imageUri: '',
        templateText: '',
      });

      onHide();
    }, [onHide]);

    const handleCloseDialog = useCallback(
      (event: unknown, reason: DialogOnCloseReason) => {
        if (reason && reason === DialogOnCloseReason.BackDropClick) {
          return;
        }

        closeDialog();
      },
      [closeDialog],
    );

    const handleCloseButton = useCallback(() => {
      closeDialog();
    }, [closeDialog]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = event.target;
        setTemplateEdit((prev) => ({
          ...prev,
          [name]: value,
        }));
      },
      [],
    );

    const handleSubmitClick = useCallback(async () => {
      if (isValidForm && notificationStore.selectedTemplate) {
        const updatedTemplate: NotificationTemplate = {
          ...notificationStore.selectedTemplate,
          imageUri: templateEdit.imageUri.trim() || null,
          templateText: templateEdit.templateText.trim() || null,
        };

        const result = await notificationStore.updateTemplateText(updatedTemplate);
        if (result) {
          onHide();
        }
      }
    }, [templateEdit, notificationStore, isValidForm, onHide]);

    if (!notificationStore.selectedTemplate) {
      return null;
    }

    return (
      <Dialog
        open={visible}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        dialogTitle={title}
        titleWeight="bold"
        disableEscapeKeyDown
      >
        <DialogContent>
          <Space height={1} />
          <Grid direction="column" gap="10px">
            {/* Template Info Display */}
            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('template_id')}:</NoWrappingText>
              <Text>{notificationStore.selectedTemplate.templateId}</Text>
            </Grid>
            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('channel')}:</NoWrappingText>
              <Text>{notificationStore.selectedTemplate.channel}</Text>
            </Grid>
            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('language')}:</NoWrappingText>
              <Text>{notificationStore.selectedTemplate.language}</Text>
            </Grid>
            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('provider_name')}:</NoWrappingText>
              <Text>{notificationStore.selectedTemplate.providerName}</Text>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Editable Fields */}
            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('image_uri')}:</NoWrappingText>
              <Input
                name="imageUri"
                label={t('image_uri')}
                id="imageUri"
                value={templateEdit.imageUri}
                variant="outlined"
                type="text"
                fullWidth
                onChange={handleChange}
                placeholder={t('image_uri')}
              />
            </Grid>
            <Grid direction="row" gap="10px" alignSelf="start" grow>
              <NoWrappingText>{t('template_text')}:</NoWrappingText>
              <StyledTextArea
                name="templateText"
                id="templateText"
                value={templateEdit.templateText}
                onChange={handleChange}
                placeholder={t('template_text')}
              />
            </Grid>
          </Grid>
          <Space height={1} />
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button onClick={handleSubmitClick} disabled={!isValidForm}>
            <Text textcolor={theme.color.white} size="sm">
              {t('ok')}
            </Text>
          </Button>
          <Button onClick={handleCloseButton}>
            <Text textcolor={theme.color.white} size="sm">
              {t('cancel')}
            </Text>
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

export default NotificationTemplateEditDialog;
